// Kalendář (docs/SPEC.md 2.7): události navázané na zakázky a klienty, přiřazené členům firmy.
import { z } from 'zod'
import { eventTypeSchema } from './enums'

const isoDate = z.string().datetime({ offset: true })

export const createEventSchema = z
  .object({
    type: eventTypeSchema.default('visit'),
    title: z.string().trim().min(2, 'Zadej název').max(200),
    startAt: isoDate,
    endAt: isoDate,
    allDay: z.boolean().default(false),
    jobId: z.string().uuid().optional().nullable(),
    clientId: z.string().uuid().optional().nullable(),
    location: z.string().trim().max(240).optional().or(z.literal('')),
    assigneeId: z.string().uuid().optional().nullable(),
  })
  .refine((v) => new Date(v.endAt).getTime() > new Date(v.startAt).getTime(), {
    message: 'Konec musí být po začátku',
    path: ['endAt'],
  })
export type CreateEventInput = z.infer<typeof createEventSchema>

export const updateEventSchema = z.object({
  type: eventTypeSchema.optional(),
  title: z.string().trim().min(2).max(200).optional(),
  startAt: isoDate.optional(),
  endAt: isoDate.optional(),
  allDay: z.boolean().optional(),
  jobId: z.string().uuid().optional().nullable(),
  clientId: z.string().uuid().optional().nullable(),
  location: z.string().trim().max(240).optional().or(z.literal('')),
  assigneeId: z.string().uuid().optional().nullable(),
})
export type UpdateEventInput = z.infer<typeof updateEventSchema>

/** Rozsah max. 62 dní (měsíc s okrajem), bez kurzoru. */
export const listEventsQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    assigneeId: z.string().uuid().optional(),
    jobId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
  })
  .refine(
    (v) => {
      const span = new Date(v.to).getTime() - new Date(v.from).getTime()
      return span > 0 && span <= 62 * 24 * 3600_000
    },
    { message: 'Rozsah musí být kladný a nejvýš 62 dní', path: ['to'] },
  )
export type ListEventsQuery = z.infer<typeof listEventsQuerySchema>

export const conflictsQuerySchema = z.object({
  startAt: isoDate,
  endAt: isoDate,
  assigneeId: z.string().uuid().optional(),
  excludeId: z.string().uuid().optional(),
})
export type ConflictsQuery = z.infer<typeof conflictsQuerySchema>

export interface EventItem {
  id: string
  type: 'visit' | 'work' | 'meeting' | 'other'
  title: string
  startAt: string
  endAt: string
  allDay: boolean
  jobId: string | null
  jobTitle: string | null
  clientId: string | null
  clientName: string | null
  location: string | null
  assigneeId: string | null
  assigneeName: string | null
  sourceInteractionId: string | null
  sourceQuote: string | null
  /** Externí kalendář (úkol 4.2): google | microsoft, null = jen ve Wac Buddy. Platí pro importované i odeslané. */
  externalProvider: string | null
  /** true = událost přišla z externího kalendáře (importovaná); odeslané naše události mají jen externalProvider. */
  imported: boolean
  createdAt: string
  updatedAt: string
}

/** Překryv událostí jednoho člena: varování, ne blokace (SPEC 2.7). */
export interface EventWarning {
  code: 'overlap'
  eventId: string
  title: string
  startAt: string
  endAt: string
}

export interface EventMutationResponse {
  event: EventItem
  warnings: EventWarning[]
}

/** GET /v1/org/members: členové firmy pro přiřazení (kalendář, zakázky, úkoly). */
export interface OrgMember {
  userId: string
  name: string
  email: string
  role: string
}
